<script setup lang="ts">
defineProps<{ data: CtaProps; chapterIndex?: number; template?: 'page' | 'overlay' }>()

const themes = [
  'bg-black text-white aspect-[300/360] lg:aspect-[355/380]',
  'bg-white text-black aspect-[300/400] lg:aspect-[355/460]',
  'bg-neon-yellow text-black aspect-[300/370] lg:aspect-[355/420]',
]

const cardsRef = useTemplateRef('cards')
const titles = useTemplateRef('titles')
const ctaSection = ref<HTMLElement | null>(null)

// ------- GSAP ANIMATION -------
const { $gsap, $ScrollTrigger } = useNuxtApp()

async function cleanupAnimation(): Promise<void> {
  $ScrollTrigger.getById('cta')?.kill()

  if (cardsRef.value?.length > 0) {
    $gsap.set(cardsRef.value, { clearProps: 'all' })
  }

  await Promise.resolve()
}

async function initAnimation() {
  if (cardsRef.value?.length <= 0) return
  const tl = $gsap.timeline({
    scrollTrigger: {
      id: 'cta',
      trigger: ctaSection.value,
      // markers: true,
      start: `top 80%`,
      end: `bottom top`,
      toggleActions: 'play reverse play reverse',
    },
  })
  tl.fromTo(
    cardsRef.value,
    {
      yPercent: 100,
    },
    {
      yPercent: 0,
      ease: 'power2.inOut',
      stagger: 0.1,
    },
  ).fromTo(
    titles.value,
    {
      clipPath: 'polygon(-100% 0%, 0% 0%, -05% 100%, -100% 100%)',
    },
    {
      clipPath: 'polygon(-100% 0%, 105% 0%, 100% 100%, -100% 100%)',
      ease: 'power2.inOut',
      stagger: 0.2,
    },
    '+=.25',
  )
}

onMounted(async () => {
  await nextTick()
  await cleanupAnimation()
  await initAnimation()
})

onBeforeUnmount(async () => {
  await cleanupAnimation()
})
</script>

<template>
  <section
    class="pt-25 lg:pt-40"
    :class="{
      'bg-white': template !== 'page' && template !== 'overlay' && data.theme !== 'light-grey',
      'bg-light-grey':
        template === 'page' || (template !== 'overlay' && data.theme === 'light-grey'),
    }"
  >
    <div class="px-bleed flex flex-col items-center text-center">
      <ElementsChapter
        v-if="data.trumpet && chapterIndex"
        :chapter="data.trumpet"
        :index="chapterIndex"
      />
      <ElementsTitleRevealv2
        :title="data.title"
        class="font-display mt-6 max-w-[14em] text-3xl uppercase font-stretch-expanded lg:mt-7 lg:text-5xl"
      />
    </div>

    <ul
      ref="ctaSection"
      class="no-scrollbar group/links scroll-px-bleed flex snap-x items-end overflow-y-clip pt-9 max-lg:overflow-x-auto lg:mx-auto lg:max-w-xl lg:justify-center lg:pt-20"
    >
      <li
        v-for="(item, idx) in data.items"
        :key="idx"
        ref="cards"
        :class="themes[idx]"
        class="relative flex w-3/4 shrink-0 snap-start flex-col items-start p-4 transition-all duration-600 lg:-mx-10 lg:w-xs lg:p-6 lg:group-hover/links:mx-0 xl:w-sm"
      >
        <h3 ref="titles" class="font-display text-xl uppercase font-stretch-expanded">
          {{ item.title }}
        </h3>
        <ElementsPortableTextWrapper :content="item.text" class="pt-7" />
        <ElementsLogomarkLink
          :link="item.callToAction"
          class="mt-auto"
          :make-container-clickable="true"
          :aria-label="'Read more about ' + item.title"
        />
      </li>
    </ul>
  </section>
</template>
